/**
 * Data models for Instrument Login functionality
 * Based on SMRT Link - Audit Login - backend implementation spec
 * https://pacb.atlassian.net/wiki/spaces/SL/pages/10084188163/SMRT+Link+-+Audit+Login+-+backend+implementation+spec
 */

/**
 * InstrumentUserInfo - API response model for user metadata
 * This is returned by GET /smrt-link/instrument-auth/users
 * Note: password and salt are omitted from API responses
 */
export interface InstrumentUserInfo {
    username: string
    loginName: string
    role: string
    email?: string
    updatedAt: string // ISO date string
    expiresAt: string // ISO date string
    lastAuthorizedAt?: string // ISO date string
    isEnabled: boolean
    isLockedOut: boolean
    numFailedAttempts: number
}

/**
 * CreateUserCredentials - Request model for creating/updating user credentials
 * POST /smrt-link/instrument-auth/credentials
 */
export interface CreateUserCredentials {
    password: string // Plaintext password, must meet minPasswordLength requirement
}

/**
 * ForceExpirePassword - Request model for forcing password expiration
 * PUT /smrt-link/instrument-auth/force-expire
 */
export interface ForceExpirePassword {
    usernames: string[] // Array of usernames to force expire
}

/**
 * UnlockUserRequest - Request model for unlocking a user
 * PUT /smrt-link/instrument-auth/unlock-user
 */
export interface UnlockUserRequest {
    username: string // Login username
    reasonMessage: string // Human-readable message explaining the admin action
}

/**
 * MessageResponse - Generic response model for operations that return a message
 */
export interface MessageResponse {
    message: string
}

/**
 * InstrumentLoginConfig - Configuration parameters for instrument login
 * These are stored in the system_config table and can be updated via POST /smrt-link/config
 */
export interface InstrumentLoginConfig {
    minPasswordLength: number // Default: 8
    maxFailedLogins: number // Default: 5
    failedLoginWindowSeconds: number // Default: 900 (15 minutes)
    passwordLifetimeDays: number // Default: 180
    instrumentSessionTimeoutSeconds: number // Default: 1800 (30 minutes)
}

/**
 * Default InstrumentLoginConfig values
 */
export const DEFAULT_INSTRUMENT_LOGIN_CONFIG: InstrumentLoginConfig = {
    minPasswordLength: 8,
    passwordLifetimeDays: 180,
    maxFailedLogins: 5,
    failedLoginWindowSeconds: 900,
    instrumentSessionTimeoutSeconds: 1800
}

/**
 * Instrument connection info for login mode
 * Used in the Instruments tab of the management page
 */
export interface InstrumentConnectionInfo {
    id: string
    name: string
    serial: string
    instrumentType: string
    isConnected: boolean
    loginModeEnabled: boolean
}
